import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { StatusBadge } from '../../StatusBadge.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import type { JobChangeEntry } from './types.js';

const scalar = (value: unknown): string | null =>
  value === null || value === undefined
    ? null
    : typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
      ? String(value)
      : null;

/**
 * Job Card'ın değişiklik kaydı (DNV-CP-0206 Sec.2 [4.6] b; ADR 0133 §2): bakım aralığı ve
 * talimatındaki her değişiklik; an, kişi, o günkü **rolü** ve değişikliğin türü. Değer kısa ise
 * eski → yeni gösterilir; yöntem ve kontrol listesi gibi yapılı alanlarda yalnız alan adı. Kayıt
 * boşsa çizilmez.
 */
export function JobChangeLog({ entries }: { entries: readonly JobChangeEntry[] }) {
  const { t } = useTranslation();
  if (entries.length === 0) return null;
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.work.changeLog.${key}`, values);
  return (
    <fieldset className={classes.fieldset}>
      <legend>{k('title')}</legend>
      <ul className={work.detailList} aria-label={k('title')}>
        {entries.map((entry) => {
          const who = [
            entry.userName ?? k('unknownUser'),
            entry.role
              ? k(entry.roleSource === 'company' ? 'officeRole' : 'vesselRole', {
                  role: entry.role,
                })
              : k('noRole'),
          ].join(' · ');
          const fields = entry.fields.map((f) => {
            const label = k(`fields.${f.field}`, { defaultValue: f.field });
            const before = scalar(f.oldValue);
            const after = scalar(f.newValue);
            return before !== null || after !== null
              ? `${label}: ${before ?? '—'} → ${after ?? '—'}`
              : label;
          });
          return (
            <li key={`${entry.at}-${entry.userId}-${entry.kind}`}>
              <span className={work.detailListMain}>
                <strong>{formatDateTime(entry.at)}</strong>
                <span className={work.note}>{who}</span>
                <span className={work.note}>{fields.join('; ')}</span>
                {entry.reason ? <span className={work.note}>{entry.reason}</span> : null}
              </span>
              <span className={work.statusCell}>
                <StatusBadge tone="neutral" variant="dot">
                  {k(`kinds.${entry.kind}`)}
                </StatusBadge>
              </span>
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}
